import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { MdOutlineSystemUpdateAlt } from 'react-icons/md';
import { orderBy as lodashOrderBy } from 'lodash';

import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import Scrollbar from '../../../components/Scrollbar';
import SearchNotFound from '../../../components/SearchNotFound';
import SimplePagination from '../../../components/SimplePagination';
import { UserListHead } from '../../../components/_dashboard/user';
import { RootStyle, SearchStyle, StyledTableRow, StyledTableCell } from '../../Global/Styling';

// ── Table Head — added Actions column ─────────────────────────────────
const TABLE_HEAD = [
  { id: 'invoiceDate', label: 'Inv. Date' },
  { id: 'invoiceNo', label: 'Invoice No.' },
  { id: 'partyName', label: 'Party Name' },
  { id: 'gstin', label: 'GSTIN' },
  { id: 'taxableVal', label: 'Taxable Val.' },
  { id: 'cgst', label: 'CGST' },
  { id: 'sgst', label: 'SGST' },
  { id: 'igst', label: 'IGST' },
  { id: 'totalGst', label: 'Total GST' },
  { id: 'totalInv', label: 'Total Inv.' },
  { id: 'actions', label: 'ACTIONS' }
];

// ── Mock Data ─────────────────────────────────────────────────────────
const MOCK_INVOICES = [
  {
    id: 1,
    invoiceDate: '2024-04-05',
    invoiceNo: 'INV/24-25/001',
    partyName: 'ABC Corp Pvt Ltd',
    gstin: '27AAAAA0000A1Z5',
    taxableVal: 100000,
    cgst: 9000,
    sgst: 9000,
    igst: 0,
    totalGst: 18000,
    totalInv: 118000,
    type: 'B2B'
  },
  {
    id: 2,
    invoiceDate: '2024-04-10',
    invoiceNo: 'INV/24-25/002',
    partyName: 'XYZ Trading Ltd',
    gstin: '09BBBBB1111B2Z6',
    taxableVal: 200000,
    cgst: 0,
    sgst: 0,
    igst: 36000,
    totalGst: 36000,
    totalInv: 236000,
    type: 'B2C'
  },
  {
    id: 3,
    invoiceDate: '2024-05-02',
    invoiceNo: 'INV/24-25/003',
    partyName: 'Ramesh Enterprises',
    gstin: '33CCCCC2222C3Z7',
    taxableVal: 75000,
    cgst: 6750,
    sgst: 6750,
    igst: 0,
    totalGst: 13500,
    totalInv: 88500,
    type: 'B2B'
  },
  {
    id: 4,
    invoiceDate: '2024-05-18',
    invoiceNo: 'INV/24-25/004',
    partyName: 'Global Tech Pvt',
    gstin: '06DDDDD3333D4Z8',
    taxableVal: 320000,
    cgst: 0,
    sgst: 0,
    igst: 57600,
    totalGst: 57600,
    totalInv: 377600,
    type: 'B2C'
  }
];

const MONTHS = [
  { v: '', label: 'All Months' },
  { v: '04', label: 'April' },
  { v: '05', label: 'May' },
  { v: '06', label: 'June' },
  { v: '07', label: 'July' },
  { v: '08', label: 'August' },
  { v: '09', label: 'September' },
  { v: '10', label: 'October' },
  { v: '11', label: 'November' },
  { v: '12', label: 'December' },
  { v: '01', label: 'January' },
  { v: '02', label: 'February' },
  { v: '03', label: 'March' }
];
const FY_LIST = ['2024-25', '2023-24', '2022-23'];
const ACCEPTED = '.json,.xlsx,.xls,.csv';

export default function GSTInvoice() {
  const navigate = useNavigate();

  // ── Table state ──
  const [filterName, setFilterName] = useState('');
  const [selectedType, setSelectedType] = useState(' ');
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('invoiceDate');

  // ── Export menu ──
  const [exportAnchor, setExportAnchor] = useState(null);

  // ── Filter state ──
  const [filterMonth, setFilterMonth] = useState('');
  const [filterFY, setFilterFY] = useState(' ');

  // ── Download dialog ──
  const [dlOpen, setDlOpen] = useState(false);
  const [dlYear, setDlYear] = useState('2024-25');
  const [dlMonth, setDlMonth] = useState('');

  // ── Upload GSTR-1 dialog ──
  const [uploadOpen, setUploadOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDone, setUploadDone] = useState(false);

  // ── PDF Invoice Preview dialog ──
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfRecord, setPdfRecord] = useState(null);

  const handleRequestSort = (_, property) => {
    setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filtered = useMemo(() => {
    let data = [...MOCK_INVOICES];
    if (filterName)
      data = data.filter(
        (r) =>
          r.invoiceNo.toLowerCase().includes(filterName.toLowerCase()) ||
          r.partyName.toLowerCase().includes(filterName.toLowerCase())
      );
    if (filterMonth) data = data.filter((r) => r.invoiceDate.slice(5, 7) === filterMonth);
    return lodashOrderBy(data, [orderBy], [order]);
  }, [filterName, filterMonth, order, orderBy]);

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (file) setUploadFile(file);
  };
  const handleUploadSubmit = () => {
    if (!uploadFile) return;
    console.log('Upload GSTR-1:', uploadFile.name);
    setUploadDone(true);
  };
  const resetUpload = () => {
    setUploadOpen(false);
    setUploadFile(null);
    setUploadDone(false);
  };
  const handleExport = (type) => {
    setExportAnchor(null);
    console.log('Export GST Invoice as', type);
  };
  const openPdfPreview = (row) => {
    setPdfRecord(row);
    setPdfOpen(true);
  };

  const handleDownload = () => {
    const monthLabel = MONTHS.find((m) => m.v === dlMonth)?.label || 'All Months';
    const rows = dlMonth
      ? MOCK_INVOICES.filter((r) => r.invoiceDate.slice(5, 7) === dlMonth)
      : MOCK_INVOICES;
    const tableRows = rows
      .map(
        (r) => `
            <tr>
                <td>${r.invoiceDate}</td>
                <td><strong>${r.invoiceNo}</strong></td>
                <td>${r.partyName}</td>
                <td style="font-size:11px">${r.gstin}</td>
                <td>₹ ${(r.taxableVal || 0).toLocaleString('en-IN')}</td>
                <td>₹ ${(r.totalGst || 0).toLocaleString('en-IN')}</td>
                <td><strong>₹ ${(r.totalInv || 0).toLocaleString('en-IN')}</strong></td>
                <td><span style="color:${r.type === 'B2B' ? '#1d4ed8' : '#0891b2'}">${
          r.type
        }</span></td>
            </tr>`
      )
      .join('');
    const totalInv = rows.reduce((s, r) => s + (r.totalInv || 0), 0);
    const totalGst = rows.reduce((s, r) => s + (r.totalGst || 0), 0);
    const html = `<!DOCTYPE html><html><head><title>GST Invoices – FY ${dlYear} – ${monthLabel}</title>
        <style>
            body { font-family: Arial, sans-serif; padding: 32px; color: #1e293b; }
            h2  { color: #0d4689; margin-bottom: 4px; }
            p   { color: #64748b; font-size: 13px; margin-top: 0; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th  { background: #0d4689; color: #fff; padding: 8px 10px; text-align: left; }
            td  { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; }
            tr:nth-child(even) td { background: #f8fafc; }
            tfoot td { background: #eff6ff; font-weight: bold; border-top: 2px solid #0d4689; }
            @media print { body { padding: 16px; } }
        </style></head><body>
        <h2>GST Invoice Report</h2>
        <p>Financial Year: <strong>FY ${dlYear}</strong> &nbsp;|&nbsp; Month: <strong>${monthLabel}</strong> &nbsp;|&nbsp; Generated: <strong>${new Date().toLocaleDateString(
      'en-IN'
    )}</strong> &nbsp;|&nbsp; Total Records: <strong>${rows.length}</strong></p>
        <table>
            <thead><tr><th>Date</th><th>Invoice No.</th><th>Party Name</th><th>GSTIN</th><th>Taxable Value</th><th>Total GST</th><th>Total Invoice</th><th>Type</th></tr></thead>
            <tbody>${tableRows}</tbody>
            <tfoot><tr><td colspan="4">Grand Total</td><td></td><td>₹ ${totalGst.toLocaleString(
              'en-IN'
            )}</td><td>₹ ${totalInv.toLocaleString('en-IN')}</td><td></td></tr></tfoot>
        </table>
        <script>window.onload = () => { window.print(); }<\/script>
        </body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setDlOpen(false);
  };

  const handleDownloadInvoice = (row) => {
    const content = `Invoice No.,Party,GSTIN,Taxable Value,Total GST,Total Invoice\n${row.invoiceNo},${row.partyName},${row.gstin},${row.taxableVal},${row.totalGst},${row.totalInv}`;
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${row.invoiceNo.replace(/\//g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Card>
        {/* ── Collapsible Filter Bar ── */}
        {isOpenFilter && (
          <Box px={3} pt={2} pb={2} sx={{ borderBottom: '1px solid #eaedf1', bgcolor: '#fafbfc' }}>
            <Grid container spacing={2} alignItems="flex-end">
              {/* Month */}
              <Grid item xs={12} sm={4}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Month
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m.v} value={m.v} sx={{ fontSize: 13 }}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Financial Year */}
              <Grid item xs={12} sm={4}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Financial Year
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={filterFY}
                    onChange={(e) => setFilterFY(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" " sx={{ fontSize: 13 }}>
                      All Years
                    </MenuItem>
                    {FY_LIST.map((fy) => (
                      <MenuItem key={fy} value={fy} sx={{ fontSize: 13 }}>
                        FY {fy}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Reset */}
              <Grid item xs="auto">
                <Tooltip title="Reset">
                  <IconButton
                    size="small"
                    className="squareIconButton"
                    onClick={() => {
                      setFilterMonth('');
                      setFilterFY(' ');
                      setFilterName('');
                    }}
                  >
                    <FilterListOffIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── Toolbar ── */}
        <RootStyle sx={{ minHeight: 45, borderBottom: '1px solid #eaedf1' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* <Tooltip title="Export data" placement="top" arrow>
                            <IconButton className="squareIconButton" onClick={(e) => setExportAnchor(e.currentTarget)}>
                                <MdOutlineSystemUpdateAlt size={18} />
                            </IconButton>
                        </Tooltip> */}
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              PaperProps={{
                sx: { borderRadius: 1, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
              }}
            >
              <MenuItem onClick={() => handleExport('pdf')} sx={{ fontSize: 13 }}>
                <PictureAsPdfIcon sx={{ fontSize: 16, mr: 1, color: '#dc2626' }} /> Export as PDF
              </MenuItem>
              <MenuItem onClick={() => handleExport('excel')} sx={{ fontSize: 13 }}>
                <GridOnIcon sx={{ fontSize: 16, mr: 1, color: '#16a34a' }} /> Export as Excel
              </MenuItem>
            </Menu>

            {/* Download by month — opens dialog */}
            <Tooltip title="Download Monthly Data" placement="top" arrow>
              <IconButton className="squareIconButton" onClick={() => setDlOpen(true)}>
                <DownloadIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>

            {/* Add Invoice — icon only */}
            <Tooltip title="Add Invoice" placement="top" arrow>
              <IconButton
                className="squareIconButton"
                onClick={() => navigate('/accounting/gst/invoice/add')}
              >
                <AddIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>

            {/* Upload GSTR-1 — icon only */}
            <Tooltip title="Upload GSTR-1" placement="top" arrow>
              <IconButton
                className="squareIconButton"
                onClick={() => {
                  setUploadOpen(true);
                  setUploadDone(false);
                  setUploadFile(null);
                }}
              >
                <UploadFileIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
            <SearchStyle
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Search invoice, party…"
              startAdornment={
                <InputAdornment position="start">
                  <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
            />
            <Tooltip title="Filters">
              <IconButton className="squareIconButton" onClick={() => setOpenFilter(!isOpenFilter)}>
                <Icon icon={roundFilterList} />
              </IconButton>
            </Tooltip>
          </Stack>
        </RootStyle>

        {/* ── Table ── */}
        <Scrollbar>
          <TableContainer sx={{ minWidth: 950, height: '50vh', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={filtered.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filtered.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell>{row.invoiceDate}</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 600, color: '#0d4689' }}>
                      {row.invoiceNo}
                    </StyledTableCell>
                    <StyledTableCell>{row.partyName}</StyledTableCell>
                    <StyledTableCell sx={{ fontSize: 10 }}>{row.gstin}</StyledTableCell>
                    <StyledTableCell>₹ {row.taxableVal.toLocaleString()}</StyledTableCell>
                    <StyledTableCell>
                      {row.cgst ? `₹ ${row.cgst.toLocaleString()}` : '–'}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.sgst ? `₹ ${row.sgst.toLocaleString()}` : '–'}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.igst ? `₹ ${row.igst.toLocaleString()}` : '–'}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 700 }}>
                      ₹ {row.totalGst.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 700 }}>
                      ₹ {row.totalInv.toLocaleString()}
                    </StyledTableCell>

                    {/* ACTIONS — PDF view only */}
                    <StyledTableCell width={60}>
                      <Tooltip title="View PDF Invoice">
                        <IconButton
                          size="small"
                          className="squareIconButton"
                          onClick={() => openPdfPreview(row)}
                        >
                          <PictureAsPdfIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              {filtered.length === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={11} sx={{ py: 4 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <SimplePagination page={page} setPage={setPage} hasNextPage={false} />
      </Card>

      {/* ═══════════════════════════════════════════════════════
               PDF INVOICE PREVIEW DIALOG
            ═══════════════════════════════════════════════════════ */}
      <Dialog open={pdfOpen} onClose={() => setPdfOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0d4689' }}>
            Invoice — {pdfRecord?.invoiceNo}
          </Typography>
          <IconButton size="small" onClick={() => setPdfOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {/* ── Simulated Invoice Preview ── */}
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
            {/* Header strip */}
            <Box
              sx={{
                bgcolor: '#0d4689',
                px: 3,
                py: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box>
                <Typography
                  sx={{ color: '#4fbbc8', fontWeight: 800, fontSize: 18, letterSpacing: 1 }}
                >
                  TAX INVOICE
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                  GST Sales Invoice
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                  {pdfRecord?.invoiceNo}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                  Date: {pdfRecord?.invoiceDate}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ p: 3 }}>
              {/* Bill To */}
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: '#718096',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      mb: 0.5
                    }}
                  >
                    Bill To
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                    {pdfRecord?.partyName}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                    GSTIN: {pdfRecord?.gstin}
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: '#718096',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      mb: 0.5
                    }}
                  >
                    Invoice Type
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{pdfRecord?.type}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* Amounts table */}
              <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 2 }}>
                {[
                  { label: 'Taxable Value', value: `₹ ${pdfRecord?.taxableVal?.toLocaleString()}` },
                  {
                    label: 'CGST',
                    value: pdfRecord?.cgst ? `₹ ${pdfRecord.cgst.toLocaleString()}` : '–'
                  },
                  {
                    label: 'SGST',
                    value: pdfRecord?.sgst ? `₹ ${pdfRecord.sgst.toLocaleString()}` : '–'
                  },
                  {
                    label: 'IGST',
                    value: pdfRecord?.igst ? `₹ ${pdfRecord.igst.toLocaleString()}` : '–'
                  },
                  { label: 'Total GST', value: `₹ ${pdfRecord?.totalGst?.toLocaleString()}` }
                ].map((item) => (
                  <Stack key={item.label} direction="row" justifyContent="space-between" mb={0.8}>
                    <Typography sx={{ fontSize: 12, color: '#64748b' }}>{item.label}</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                      {item.value}
                    </Typography>
                  </Stack>
                ))}
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0d4689' }}>
                    Total Invoice Amount
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0d4689' }}>
                    ₹ {pdfRecord?.totalInv?.toLocaleString()}
                  </Typography>
                </Stack>
              </Box>

              {/* Footer note */}
              <Typography
                sx={{
                  mt: 2,
                  fontSize: 10,
                  color: '#94a3b8',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                This is a computer-generated invoice. No signature required.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button
            onClick={() => setPdfOpen(false)}
            sx={{ textTransform: 'none', color: '#64748b' }}
          >
            Close
          </Button>
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => window.print()}
            sx={{ textTransform: 'none' }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            className="mainBtn"
            startIcon={<DownloadIcon />}
            onClick={() => {
              handleDownloadInvoice(pdfRecord);
              setPdfOpen(false);
            }}
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════
               UPLOAD GSTR-1 DIALOG
            ═══════════════════════════════════════════════════════ */}
      <Dialog open={uploadOpen} onClose={resetUpload} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0d4689' }}>
            Upload GSTR-1
          </Typography>
          <IconButton size="small" onClick={resetUpload}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {!uploadDone ? (
            <Box pt={1}>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, fontSize: 13 }}>
                Upload your GSTR-1 file from the GST portal. Accepted: JSON, Excel (.xlsx/.xls),
                CSV.
              </Typography>
              <Box
                onDrop={handleFileDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                component="label"
                htmlFor="gstr1-upload-input"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px dashed ${dragOver ? '#0d4689' : '#cbd5e1'}`,
                  borderRadius: 2,
                  p: 4,
                  cursor: 'pointer',
                  bgcolor: dragOver ? '#eff6ff' : '#f8fafc',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  mb: 2
                }}
              >
                <input
                  id="gstr1-upload-input"
                  type="file"
                  accept={ACCEPTED}
                  hidden
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
                <CloudUploadIcon
                  sx={{ fontSize: 40, color: dragOver ? '#0d4689' : '#94a3b8', mb: 1 }}
                />
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>
                  Drag & drop your file here
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5 }}>
                  or click to browse — JSON / XLSX / XLS / CSV
                </Typography>
              </Box>
              {uploadFile && (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#eff6ff',
                    borderRadius: 1,
                    border: '1px solid #bfdbfe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8' }}>
                    📄 {uploadFile.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {(uploadFile.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box pt={2} textAlign="center">
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#16a34a', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#15803d', mb: 0.5 }}>
                File Uploaded Successfully!
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: 13 }}>
                <strong>{uploadFile?.name}</strong> has been uploaded. Data will be reflected
                shortly.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          {!uploadDone ? (
            <>
              <Button onClick={resetUpload} sx={{ textTransform: 'none', color: '#64748b' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                className="mainBtn"
                disabled={!uploadFile}
                onClick={handleUploadSubmit}
                startIcon={<CloudUploadIcon />}
                sx={{ textTransform: 'none' }}
              >
                Upload
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              className="mainBtn"
              onClick={resetUpload}
              sx={{ textTransform: 'none' }}
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════
               DOWNLOAD MONTHLY DATA DIALOG
            ══════════════════════════════════════════════ */}
      <Dialog open={dlOpen} onClose={() => setDlOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0d4689' }}>
            Download GST Invoices
          </Typography>
          <IconButton size="small" onClick={() => setDlOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, fontSize: 13 }}>
              Select Financial Year and Month to generate and download a PDF report.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Financial Year
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={dlYear}
                    onChange={(e) => setDlYear(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {FY_LIST.map((fy) => (
                      <MenuItem key={fy} value={fy} sx={{ fontSize: 13 }}>
                        FY {fy}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Month
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={dlMonth}
                    onChange={(e) => setDlMonth(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m.v} value={m.v} sx={{ fontSize: 13 }}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button onClick={() => setDlOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className="mainBtn"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
